import { Box, useToast, VStack, Flex, Spinner } from "@chakra-ui/react";
import { Navigate, useNavigate, useParams } from "react-router";

import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { useSavedProfile, useUpdateSavedProfile } from "@equidefi/portals/hooks/useSavedProfiles";

import { ProfileNavButton, VaultContainer } from "@/pages/app/vault/v2/components";
import {
  EditCard,
  AccountTypeForm,
  AccreditationForm,
  GovernmentIdForm,
  HomeAddressForm,
  PersonalDetailsForm,
} from "../components";

const SECTION_CONFIG = {
  "personal-details": {
    title: "Personal Details",
    description: "Update the personal information saved for this profile.",
    component: PersonalDetailsForm,
  },
  "government-id": {
    title: "Government ID",
    description: "Update identification details for this profile.",
    component: GovernmentIdForm,
  },
  "home-address": {
    title: "Home Address",
    description: "Update the residential address saved for this profile.",
    component: HomeAddressForm,
  },
  "account-type": {
    title: "Account Type",
    description: "Update the account type for this saved profile.",
    component: AccountTypeForm,
  },
  accreditation: {
    title: "Accreditation",
    description: "Update accreditation details for this profile.",
    component: AccreditationForm,
  },
} as const;

type SectionKey = keyof typeof SECTION_CONFIG;

const isSectionKey = (value: string | undefined): value is SectionKey => !!value && value in SECTION_CONFIG;

const SavedProfileEdit = () => {
  const { id, section } = useParams<{ id: string; section: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const isValidSection = isSectionKey(section);
  const sectionConfig = isValidSection ? SECTION_CONFIG[section] : null;

  const backPath = `/vault/profile/saved-profiles/${id}`;

  useDocumentTitle([sectionConfig?.title ?? "Saved Profile", "Saved Profile"]);

  const { data: profile, isLoading, isError } = useSavedProfile(id ?? "");

  const { mutateAsync: updateSavedProfile } = useUpdateSavedProfile(id ?? "");

  if (!id || !sectionConfig) {
    return <Navigate to={id ? backPath : "/vault/profile/saved-profiles"} replace />;
  }

  const { title, description, component: SectionForm } = sectionConfig;

  if (isLoading) {
    return (
      <VaultContainer>
        <Flex justify="center" py="10">
          <Spinner />
        </Flex>
      </VaultContainer>
    );
  }

  if (isError) {
    throw new Error("Failed to load saved profile");
  }

  if (!profile) {
    return <Navigate to="/vault/profile/saved-profiles" replace />;
  }

  const handleSave = async (values: Record<string, any>) => {
    await updateSavedProfile(values);

    toast({
      status: "success",
      description: "Saved profile updated successfully.",
    });

    navigate(backPath);
  };

  return (
    <VaultContainer>
      <VStack align="stretch" spacing="4" w="full">
        <ProfileNavButton to={backPath} layout="rtl">
          Back To Saved Profile
        </ProfileNavButton>

        <EditCard title={title} description={description} />

        <SectionForm profile={profile} onSave={handleSave} />
      </VStack>
    </VaultContainer>
  );
};

export default SavedProfileEdit;
